import { BellRinging } from "@phosphor-icons/react";
import { SectionTitle } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

export const ALERTS = [
  { k: "new_request", label: "New valuation request from the website", who: "owner" },
  { k: "job_assigned", label: "A job is assigned to me", who: "all" },
  { k: "daily_digest", label: "Morning digest of new requests & follow-ups", who: "owner" },
  { k: "due_today", label: "Jobs due today", who: "all" },
  { k: "sla_breach", label: "Deadline missed / at risk", who: "owner" },
  { k: "payment_received", label: "Payment received on an invoice", who: "owner" },
  { k: "invoice_overdue", label: "Invoice went overdue", who: "owner" },
  { k: "empanelment_renewal", label: "Empanelment coming up for renewal", who: "owner" },
];

export const NOTIF_DEFAULTS = {
  channels: { email: true, whatsapp: false, sms: false },
  whatsapp_number: "",
  alerts: Object.fromEntries(ALERTS.map((a) => [a.k, true])),
};

export function mergeNotifPrefs(data) {
  return {
    ...NOTIF_DEFAULTS,
    ...(data || {}),
    channels: { ...NOTIF_DEFAULTS.channels, ...(data?.channels || {}) },
    alerts: { ...NOTIF_DEFAULTS.alerts, ...(data?.alerts || {}) },
  };
}

const CHANNELS = [
  { k: "email", label: "Email", note: "Working now — used for digests, assignments and invoices" },
  { k: "whatsapp", label: "WhatsApp", note: "Needs Twilio credentials (see Integrations). Off until then." },
  { k: "sms", label: "SMS", note: "No SMS provider connected yet" },
];

export default function NotificationPrefsEditor({ value, onChange, role = "owner", readOnly = false }) {
  const form = value;
  const visible = ALERTS.filter((a) => a.who === "all" || role === "owner");
  const set = (patch) => !readOnly && onChange({ ...form, ...patch });

  return (
    <div className="space-y-6">
      <Card className="p-6 border-border space-y-5">
        <SectionTitle icon={BellRinging}>Channels</SectionTitle>
        {CHANNELS.map((c) => (
          <div key={c.k} className="flex items-start justify-between gap-4">
            <div><Label className="text-sm">{c.label}</Label><p className="text-xs text-muted-foreground mt-0.5">{c.note}</p></div>
            <Switch checked={!!form.channels[c.k]} disabled={readOnly} onCheckedChange={(v) => set({ channels: { ...form.channels, [c.k]: v } })} data-testid={`np-channel-${c.k}`} />
          </div>
        ))}
        <div className="space-y-1.5 pt-2 border-t border-border">
          <Label>WhatsApp number</Label>
          <Input value={form.whatsapp_number} disabled={readOnly} onChange={(e) => set({ whatsapp_number: e.target.value })} placeholder="+91 98261 XXXXX" data-testid="np-whatsapp-number" />
          <p className="text-xs text-muted-foreground">Where WhatsApp alerts will go once Twilio is connected.</p>
        </div>
      </Card>

      <Card className="p-6 border-border space-y-3">
        <SectionTitle>Alert types</SectionTitle>
        <div className="space-y-2">
          {visible.map((a) => (
            <div key={a.k} className="flex items-center justify-between gap-3 p-3 rounded-md bg-muted border border-border">
              <span className="text-sm text-primary">{a.label}</span>
              <Switch checked={!!form.alerts[a.k]} disabled={readOnly} onCheckedChange={(v) => set({ alerts: { ...form.alerts, [a.k]: v } })} data-testid={`np-alert-${a.k}`} />
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
